/**
 * The main nav, collapsed behind a button when the screen is too narrow for it.
 *
 * WHY THIS EXISTS
 *
 * Twelve destinations do not fit beside New Entry and the profile below about
 * 760px. Before this they wrapped -- three rows of links on a phone, most of
 * the screen spent on navigation before any books appeared. Wrapping was the
 * right stopgap on 2026-09-19 and it is not a mobile navigation.
 *
 * IT IS THE SAME NAV, NOT A SECOND COPY OF IT.
 *
 * The links are passed in as children, so `AppShell` still declares the list
 * once and `NavItem` still decides which one is current. A component that held
 * its own copy of the twelve destinations would drift from the bar it replaces,
 * and the drift would show up as a menu missing the screen somebody just added.
 *
 * WHAT DECIDES WHICH FORM IS SHOWN IS CSS, NOT JAVASCRIPT.
 *
 * Both the button and the nav render always; `styles.css` hides whichever does
 * not apply at the current width. A `matchMedia` listener in here would be a
 * second source of truth for the breakpoint, it would flicker on first paint
 * before the listener runs, and jsdom implements none of it -- so every test
 * would be exercising a branch no browser takes. This way the tests see one
 * DOM and the browser picks.
 *
 * Dismissal is NewEntryMenu's, deliberately unchanged: mousedown rather than
 * click so dismissing the menu and pressing what you were aiming at is one
 * click, plus Escape. It also closes on navigation -- a menu that stays open
 * over the screen it just opened is the commonest bug in this pattern, and
 * `useLocation` is what notices.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * `badge`, when given, sits inside the button: on a phone the tabs are behind
 * it, so a count they carry has to be on the button too.
 */
export function NavMenu({ children, badge }: { children: ReactNode; badge?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!open) return;

    function onPointer(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
   * Closed on every navigation. Keyed on the path rather than on the click,
   * because a link inside the panel is not the only way out of a screen --
   * the browser's back button is another, and it leaves the panel open over
   * whatever it lands on.
   */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="nav-menu" ref={root}>
      <button
        type="button"
        className="nav-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="main-nav"
        onClick={() => setOpen((was) => !was)}
      >
        Menu ▾{badge}
      </button>
      {/*
        `id` is what `aria-controls` above points at, and the class says which
        form this is in. The links themselves are unchanged in either.
      */}
      <nav id="main-nav" className={`row nav${open ? ' is-open' : ''}`}>
        {children}
      </nav>
    </div>
  );
}
